/**
 * web/src/games/ResultCard.tsx — what the guess was worth (card eq92fFXqQUuP).
 *
 * exact = 2, close = 1, miss = 0. The verdict is the server's; the three
 * buttons here only ever *overrule* it, and only when a model produced it —
 * a mechanical three-choice pick or a partner's earlier override is final, so
 * nothing here can be tapped twice to move a score.
 */
import type { GuessMeRound, GuessMeVerdict } from '../../../shared/types';
import { GUESS_ME_CATEGORY_LABELS, GUESS_ME_SCORE } from '../../../shared/types';

interface ResultCardProps {
  round: GuessMeRound;
  onOverride: (verdict: GuessMeVerdict) => void;
}

const VERDICT_COPY: Record<GuessMeVerdict, string> = {
  exact: 'You know each other.',
  close: 'Close — you were in the right neighbourhood.',
  miss: 'Not this one. You will get the next one.',
};

const SOURCE_COPY: Record<string, string> = {
  ai: 'Scored by the concierge.',
  match: 'Scored from the three choices.',
  /* A typed guess the assistant could not judge, compared word for word
     instead — an honest fallback, never dressed up as a choice (sol review
     2026-10-19). It stays correctable. */
  fallback: 'Compared word for word — the concierge could not score it.',
  override: 'Scored by hand.',
};

export function ResultCard({ round, onOverride }: ResultCardProps) {
  const verdict = round.verdict;
  if (!verdict) return null;
  const source = round.verdictSource ?? 'ai';
  const category = GUESS_ME_CATEGORY_LABELS[round.category] ?? round.category;

  return (
    <section className="gm-result" id="gm-result" data-verdict={verdict} data-source={source}>
      <p className="gm-result-verdict" id="gm-verdict">
        {VERDICT_COPY[verdict]}
      </p>
      <p className="gm-result-score" id="gm-score">
        +{GUESS_ME_SCORE[verdict]} point{GUESS_ME_SCORE[verdict] === 1 ? '' : 's'}
        <span className="gm-result-total">
          {' '}
          · {round.categoryScore} in {category}
        </span>
      </p>
      <p className="gm-result-source" id="gm-source">
        {SOURCE_COPY[source] ?? SOURCE_COPY.ai}
        {/* Game answers are no longer saved to the Notebook (Jonathan
            2026-10-23), so the panel never mentions a notebook note —
            there is nothing to promise or to apologise for. */}
      </p>

      {/* An assistant verdict — or a straight compare that stood in for one —
          can be overruled. A pick from the three choices cannot: that was
          decided by comparing two strings, and it is final. */}
      {(source === 'ai' || source === 'fallback') && (
        <div className="gm-overrides" role="group" aria-label="Change the score">
          <span className="gm-overrides-label">Not right?</span>
          {(['exact', 'close', 'miss'] as GuessMeVerdict[]).map(v => (
            <button
              key={v}
              type="button"
              className="pear-btn gm-override"
              id={`gm-override-${v}`}
              onClick={() => onOverride(v)}
            >
              {v}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
